import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Mail } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();
  const appName = config.app_name || "Mailwave";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white dark:bg-neutral-950">
      <div className="hidden lg:flex relative bg-neutral-50 dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 items-center justify-center p-12">
        <div className="absolute top-8 left-8 flex items-center gap-2">
          <div className="h-7 w-7 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-black tracking-tight text-lg">{appName}</span>
        </div>
        <div className="max-w-md w-full flex flex-col items-center text-center">
          <div className="h-24 w-24 rounded-sm flex items-center justify-center mb-8" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Mail className="h-12 w-12 text-white" />
          </div>
        </div>
        <div className="absolute bottom-12 left-12 right-12">
          <p className="label-overline mb-3">Email Marketing</p>
          <h2 className="font-display font-black tracking-tighter text-3xl text-neutral-900 dark:text-neutral-50 leading-tight">
            Send campaigns that<br />actually convert.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <p className="label-overline mb-3">Sign in</p>
          <h1 className="font-display font-black tracking-tighter text-4xl mb-8">Welcome back.</h1>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-sm h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-sm h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-sm btn-brand font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          {config.features?.public_registration && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-6">
              New to {appName}?{" "}
              <Link to="/register" className="font-semibold text-neutral-900 dark:text-neutral-50 underline underline-offset-4" data-testid="goto-register-link">
                Create an account
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
